import React, { useState, useEffect } from 'react';
import { Layout, Menu, message } from 'antd';
import { LoginOutlined, HomeOutlined, DashboardOutlined } from '@ant-design/icons';
import Login from './components/Login';
import DepartmentView from './components/DepartmentView';
import AdminPanel from './components/AdminPanel';
import PhdOverview from './components/PhdOverview';
import { getToken, setToken, removeToken, getCurrentUser, setCurrentUser } from './utils/auth';
import api from './utils/api';
import './App.css';

const { Header, Content, Sider } = Layout;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  const [user, setUser] = useState(getCurrentUser());
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(false);

  // 只有超级管理员（不隶属任何部门的管理员），部门只有普通用户
  const isSuperAdmin = user?.role === 'admin' && !user?.department_id;

  useEffect(() => {
    // 只在有token且已认证时加载部门列表
    if (isAuthenticated && getToken()) {
      loadDepartments();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // 当部门列表加载完成且没有选中任何内容时
    if (departments.length > 0 && !selectedDept) {
      if (isSuperAdmin) {
        // 超级管理员默认打开核心指标页面
      setSelectedDept({ type: 'indicators' });
      } else if (user?.department_id) {
        // 普通用户默认打开自己的部门页面
        const userDept = departments.find(d => d.id === user.department_id);
        if (userDept) {
          setSelectedDept(userDept);
        }
      }
    }
  }, [departments, user]);

  const loadDepartments = async () => {
    try {
      const response = await api.get('/departments');
      // 过滤掉"实验室与资产管理处"、"文献与信息中心"和"教学与科研单位"及其所有子部门
      const excludedDeptNames = [
        // 保留“职能部门”和“博士授权点”在部门列表中，用于菜单分组
        '实验室与资产管理处',
        '文献与信息中心',
        '教学与科研单位',
        '商学院',
        '大数据与软件工程学院',
        '生命学部',
        '华美生命健康学院',
        '海洋生物种业研究院',
        '动物性别与发育重点研究所',
        '国际事务处'
      ];
      const filteredDepartments = response.data.filter(
        dept => !excludedDeptNames.includes(dept.name)
      );
      setDepartments(filteredDepartments);
    } catch (error) {
      // 如果是401或422错误，可能是token问题，清除认证状态
      if (error.response?.status === 401 || error.response?.status === 422) {
        removeToken();
        setUser(null);
        setIsAuthenticated(false);
        message.error('认证失败，请重新登录');
      } else {
        message.error('加载部门列表失败');
      }
    }
  };

  const handleLogin = (token, userData) => {
    if (!token) {
      message.error('登录失败：未获取到token');
      return;
    }
    // 保存token和用户信息
    setToken(token);
    if (userData) {
      setCurrentUser(userData);
      setUser(userData);
    }
    setIsAuthenticated(true);
    message.success('登录成功');
    // 注意：loadDepartments会在useEffect中自动调用，因为isAuthenticated变为true
  };

  const handleLogout = async () => {
    console.log('Logout button clicked, calling logout API...');
    try {
      // 调用后端 API 清除 token
      const token = getToken();
      if (token) {
        console.log('Sending logout request with token:', token.substring(0, 20) + '...');
        const response = await api.post('/logout');
        console.log('Logout API response:', response.data);
      } else {
        console.warn('No token found, skipping logout API call');
      }
    } catch (error) {
      // 即使后端调用失败，也继续清除前端状态
      console.error('Logout API call failed:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      // 清除前端 token 和用户信息
      console.log('Clearing local token and user info...');
    removeToken();
    setUser(null);
    setIsAuthenticated(false);
    setSelectedDept(null);
    message.success('已退出登录');
    }
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // 获取用户可见的部门列表
  const getVisibleDepartments = () => {
    if (isSuperAdmin) {
      // 只有超级管理员可以看到所有部门
      return departments;
    }
    // 部门管理员和普通用户只能看到自己部门
    if (user?.department_id) {
      return departments.filter(dept => dept.id === user.department_id);
    }
    return [];
  };

  const visibleDepartments = getVisibleDepartments();

  // 获取所有部门ID集合，用于检查parent_id是否存在（用于其它顶级部门判断）
  const departmentIds = new Set(departments.map(d => d.id));
  
  // 明确找出“职能部门”本身及其子部门（兼容老数据和新数据）
  // 1) 优先按名称精确匹配“职能部门”
  let leadDept = departments.find(d => d.name === '职能部门');
  let leadDeptId = leadDept ? leadDept.id : null;
  let leadDeptChildren = [];

  if (leadDeptId) {
    // 有真实的“职能部门”记录：按 parent_id 分组
    leadDeptChildren = departments.filter(dept => dept.parent_id === leadDeptId);
  } else {
    // 2) 兼容旧数据：通过“parent_id 不在部门列表中的部门”推断牵头子部门
    // 这类部门的 parent_id 可以视为“职能部门”的 ID
    leadDeptChildren = departments.filter(dept =>
      dept.parent_id && !departmentIds.has(dept.parent_id)
    );
    if (leadDeptChildren.length > 0) {
      leadDeptId = leadDeptChildren[0].parent_id;
    }
  }
  
  // 找出"博士授权点"的ID及其子部门
  const phdDept = departments.find(d => d.name === '博士授权点');
  const phdDeptId = phdDept ? phdDept.id : null;
  const phdDeptChildren = departments.filter(dept => dept.parent_id === phdDeptId);
  
  // 找出真正的顶级部门：
  // - 排除“职能部门”和“博士授权点”这两个根节点
  // - 排除所有“职能部门”的子部门（它们归入“职能部门”分组）
  // - 排除所有 parent_id 为“博士授权点”的二级部门（它们归入“博士授权点”分组）
  const leadDeptChildIds = new Set(leadDeptChildren.map(d => d.id));
  const topLevelDepartments = departments.filter(dept => {
    // 排除两个根分组节点
    if (dept.name === '职能部门' || dept.name === '博士授权点') return false;
    // 排除职能部门体系下的所有业务部门
    if (leadDeptChildIds.has(dept.id)) return false;
    // 排除“博士授权点”下的二级部门（智能科学与技术、审计、生物与医药）
    if (phdDeptId && dept.parent_id === phdDeptId) return false;
    // 其它：parent_id 为 null 或父部门不在当前列表中的视为顶级
    if (!dept.parent_id || dept.parent_id === null) return true;
    return !departmentIds.has(dept.parent_id);
  });

  // 构建菜单项
  const buildMenuItems = () => {
    const items = [];
    
    // 核心指标 - 只有超级管理员能看到
    if (isSuperAdmin) {
      items.push({
      key: 'indicators',
      icon: <DashboardOutlined />,
        label: '核心指标'
      });
    }
    
    // 管理员审核 - 所有管理员（超级管理员和部门管理员）都能看到
    if (user?.role === 'admin') {
      items.push({
      key: 'admin',
      icon: <HomeOutlined />,
        label: '管理员审核'
      });
    }
    
    // "职能部门"及其子部门 - 只有超级管理员能看到
    if (isSuperAdmin && leadDeptId && leadDeptChildren.length > 0) {
      const leadDeptChildrenItems = leadDeptChildren
        .map(child => ({
          key: `dept-${child.id}`,
          label: child.name
        }));
      
      items.push({
        key: `lead-dept-${leadDeptId}`,
        icon: <HomeOutlined />,
        label: '职能部门',
        children: leadDeptChildrenItems
      });
    }
    
    // "博士授权点"及其子部门 - 只有超级管理员能看到
    if (isSuperAdmin && phdDeptId && phdDeptChildren.length > 0) {
      // 构建二级部门（智能科学与技术、审计、生物与医药）
      const phdDeptChildrenItems = phdDeptChildren
        .filter(child => {
          // 只显示二级部门（parent_id是博士授权点）
          return child.parent_id === phdDeptId;
        })
        .map(child => {
          // 查找该二级部门下的三级部门
          const grandChildren = departments
            .filter(gc => gc.parent_id === child.id)
            .map(gc => ({
              key: `dept-${gc.id}`,
              label: gc.name
            }));
          
          return {
            key: `dept-${child.id}`,
            label: child.name,
            children: grandChildren.length > 0 ? grandChildren : undefined
          };
        });
      
      items.push({
        key: `phd-dept-${phdDeptId}`,
        icon: <HomeOutlined />,
        label: '博士授权点',
        children: phdDeptChildrenItems
      });
    }
    
    // 其他顶级部门
    const otherTopLevelItems = topLevelDepartments
      .map(dept => {
        const children = departments
          .filter(child => child.parent_id === dept.id)
          .map(child => ({
            key: `dept-${child.id}`,
            label: child.name
          }))
          .filter(child => {
            // 只有超级管理员可以看到所有子部门
            if (isSuperAdmin) return true;
            // 部门管理员和普通用户只显示自己部门
          const deptId = parseInt(child.key.replace('dept-', ''));
          return deptId === user?.department_id;
      });
      
        // 检查父部门本身是否可见（超级管理员或本人部门）
        const isParentVisible = isSuperAdmin || dept.id === user?.department_id;
      
        // 如果有可见的子部门或父部门本身可见，则显示
        if (children.length > 0 || isParentVisible) {
          return {
            key: `dept-${dept.id}`,
            icon: <HomeOutlined />,
            label: dept.name,
            children: children.length > 0 ? children : undefined
          };
        }
        return null;
      })
      .filter(item => item !== null);
    
    items.push(...otherTopLevelItems);
    
    // 确保部门管理员能看到自己的部门（如果还没有在菜单中）
    if (!isSuperAdmin && user?.department_id) {
      const userDept = departments.find(d => d.id === user.department_id);
      if (userDept) {
        // 检查用户的部门是否已经在菜单中
        const userDeptInMenu = items.some(item => {
          if (item.key === `dept-${userDept.id}`) return true;
          if (item.children) {
            return item.children.some(child => child.key === `dept-${userDept.id}`);
          }
          return false;
        });
        
        // 如果用户的部门不在菜单中，直接添加
        if (!userDeptInMenu) {
          items.push({
            key: `dept-${userDept.id}`,
            icon: <HomeOutlined />,
            label: userDept.name
          });
        }
      }
    }
    return items;
  };

  const menuItems = buildMenuItems();

  const handleMenuClick = ({ key }) => {
    if (key === 'indicators') {
      // 只有超级管理员可以访问核心指标
      if (isSuperAdmin) {
      setSelectedDept({ type: 'indicators' });
      } else {
        message.error('权限不足：只有超级管理员可以访问核心指标');
      }
    } else if (key === 'admin') {
      // 只有超级管理员可以访问管理员审核
      if (isSuperAdmin) {
      setSelectedDept({ type: 'admin' });
      } else {
        message.error('权限不足：只有系统管理员可以访问此页面');
      }
    } else if (key.startsWith('dept-')) {
      const deptId = parseInt(key.replace('dept-', ''));
      const dept = departments.find(d => d.id === deptId);
      if (dept) {
        // 检查权限：非超级管理员只能访问自己部门
        if (!isSuperAdmin && dept.id !== user?.department_id) {
          message.error('权限不足：您只能访问自己部门的页面');
          return;
        }
        setSelectedDept(dept);
      }
    } else if (key.startsWith('lead-dept-')) {
      // "职能部门"菜单项被点击，不执行任何操作（只是折叠/展开）
      // 因为"职能部门"不是实际部门，不能点击进入
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#001529', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: '#fff', margin: 0 }}>博士学位授予单位与授予点建设进度管理系统</h1>
        <div style={{ color: '#fff' }}>
          <span style={{ marginRight: '20px' }}>欢迎, {user?.username}</span>
          <span style={{ marginRight: '10px' }}>({user?.role === 'admin' ? '管理员' : '普通用户'})</span>
          <LoginOutlined onClick={handleLogout} style={{ cursor: 'pointer' }} />
        </div>
      </Header>
      <Layout>
        {user?.role === 'admin' && (
        <Sider 
          collapsible 
          collapsed={menuCollapsed} 
          onCollapse={setMenuCollapsed}
          width={250}
          style={{ background: '#fff' }}
        >
          <Menu
            mode="inline"
            selectedKeys={
              selectedDept?.type === 'indicators' ? ['indicators'] :
              selectedDept?.type === 'admin' ? ['admin'] : 
              selectedDept ? [`dept-${selectedDept.id}`] : []
            }
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </Sider>
        )}
        <Content style={{ padding: '20px', background: '#f0f2f5' }}>
          {selectedDept?.type === 'indicators' ? (
            isSuperAdmin ? (
              <PhdOverview departments={visibleDepartments} user={user} />
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <h2>权限不足：只有超级管理员可以访问核心指标</h2>
              </div>
            )
          ) : selectedDept?.type === 'admin' ? (
            isSuperAdmin ? (
              <AdminPanel 
                onUserUpdate={(updatedUser) => {
                  setUser(updatedUser);
                  setCurrentUser(updatedUser);
                }}
                onNavigateToDepartment={(deptId, activeTab) => {
                  const dept = departments.find(d => d.id === deptId);
                  if (dept) {
                    setSelectedDept({ ...dept, initialTab: activeTab });
                  }
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <h2>权限不足：只有系统管理员可以访问此页面</h2>
              </div>
            )
          ) : selectedDept ? (
            // 再次检查权限：非超级管理员只能访问自己部门
            isSuperAdmin || selectedDept.id === user?.department_id ? (
            <DepartmentView 
              department={selectedDept} 
              user={user}
              initialTab={selectedDept.initialTab}
            />
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <h2>权限不足：您只能访问自己部门的页面</h2>
              </div>
            )
          ) : (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h2>{user?.role === 'admin' ? '请从左侧选择部门查看数据' : '正在加载...'}</h2>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;

